import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const uid = () => Math.random().toString(36).slice(2, 10);

const KNOWN_PATHS = new Set([
  "/", "/about", "/services", "/portfolio", "/blog", "/contact",
  "/privacy-policy", "/terms-of-service", "/cookie-policy",
]);

function classifyUrl(url) {
  const value = (url || "").trim();
  if (!value) return { ok: false, message: "URL is required" };
  if (value.includes(" ")) return { ok: false, message: "URL cannot contain spaces" };
  if (/^http:\/\//i.test(value)) return { ok: false, message: "Use https:// for external links" };
  if (/^https:\/\//i.test(value)) return { ok: true, kind: "external", message: "External link" };
  if (!value.startsWith("/")) return { ok: false, message: "Use /internal-page or https://…" };
  const path = value.split("?")[0].split("#")[0];
  if (KNOWN_PATHS.has(path) || path.startsWith("/services/") || path.startsWith("/blog/")) {
    return { ok: true, kind: "internal", known: true, message: "Known public route" };
  }
  return { ok: true, kind: "internal", known: false, message: "Path is not a known public route" };
}

export default function Navigation() {
  const [menus, setMenus] = useState([]);
  const [active, setActive] = useState("header");
  const [saved, setSaved] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);

  const fetchData = async () => {
    const { data } = await api.get("/navigation");
    setMenus(data || []);
  };
  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const onLeave = (e) => {
      if (saved) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [saved]);

  const current = menus.find((m) => m.location === active) || { location: active, items: [] };

  const setItems = (items) => {
    setSaved(false);
    setMenus((prev) => {
      const found = prev.find((m) => m.location === active);
      if (found) return prev.map((m) => (m.location === active ? { ...m, items } : m));
      return [...prev, { location: active, items }];
    });
  };

  const add = () => {
    setItems([...(current.items || []), { id: uid(), label: "New link", url: "/", order: (current.items?.length || 0) + 1, children: [] }]);
  };
  const update = (id, key, val) => setItems(current.items.map((i) => (i.id === id ? { ...i, [key]: val } : i)));
  const remove = (id) => setItems(current.items.filter((i) => i.id !== id));
  const move = (idx, dir) => {
    const items = [...current.items];
    const t = idx + dir;
    if (t < 0 || t >= items.length) return;
    [items[idx], items[t]] = [items[t], items[idx]];
    setItems(items.map((i, k) => ({ ...i, order: k + 1 })));
  };
  const moveTo = (from, to) => {
    if (from == null || from === to || from < 0 || to < 0 || to >= current.items.length) return;
    const items = [...current.items];
    const [row] = items.splice(from, 1);
    items.splice(to, 0, row);
    setItems(items.map((i, k) => ({ ...i, order: k + 1 })));
  };

  const urlCounts = (current.items || []).reduce((acc, item) => {
    const key = (item.url || "").trim();
    if (key) acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const invalidCount = (current.items || []).filter((item) => !classifyUrl(item.url).ok || urlCounts[item.url] > 1).length;

  const save = async () => {
    if (invalidCount) {
      toast.error("Fix invalid or duplicate URLs before saving");
      return;
    }
    try {
      await api.patch(`/navigation/${active}`, { items: current.items });
      setSaved(true);
      toast.success("Menu saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        title="Navigation"
        subtitle="Header and footer menus with known-route checks, duplicate warnings, and drag reorder."
        actions={<div className="flex items-center gap-3"><span className={`text-xs ${saved ? "text-emerald-300" : "text-amber-300"}`}>{saved ? "All changes saved" : "Unsaved changes"}</span><Button onClick={save} className="btn-gradient text-white border-0"><Icons.Save className="w-4 h-4 mr-1.5" /> Save menu</Button></div>}
      />

      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="glass border-white/10 mb-6">
          <TabsTrigger value="header" className="data-[state=active]:bg-white/10 data-[state=active]:text-white">Header</TabsTrigger>
          <TabsTrigger value="footer" className="data-[state=active]:bg-white/10 data-[state=active]:text-white">Footer</TabsTrigger>
        </TabsList>
        <TabsContent value={active}>
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-white capitalize">{active} menu items</h3>
              <Button onClick={add} variant="ghost" className="text-white/70 hover:text-white hover:bg-white/5">
                <Icons.Plus className="w-4 h-4 mr-1.5" /> Add link
              </Button>
            </div>
            <div className="space-y-2">
              {(current.items || []).map((it, idx) => {
                const check = classifyUrl(it.url);
                const duplicate = (urlCounts[it.url] || 0) > 1;
                return (
                  <div
                    key={it.id || `nav-item-${idx}`}
                    draggable
                    onDragStart={() => setDragIndex(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { moveTo(dragIndex, idx); setDragIndex(null); }}
                    className={`p-3 rounded-lg glass space-y-2 ${dragIndex === idx ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col">
                        <button type="button" onClick={() => move(idx, -1)} className="p-0.5 text-white/40 hover:text-white" aria-label="Move up"><Icons.ChevronUp className="w-3.5 h-3.5" /></button>
                        <button type="button" onClick={() => move(idx, 1)} className="p-0.5 text-white/40 hover:text-white" aria-label="Move down"><Icons.ChevronDown className="w-3.5 h-3.5" /></button>
                      </div>
                      <Icons.GripVertical className="w-4 h-4 text-white/30 cursor-grab" />
                      <Input value={it.label} onChange={(e) => update(it.id, "label", e.target.value)} className="max-w-xs bg-black/20 border-white/10 text-white" placeholder="Label" />
                      <div className="flex-1 min-w-0">
                        <Input value={it.url} onChange={(e) => update(it.id, "url", e.target.value)} className={`bg-black/20 text-white ${!check.ok || duplicate ? "border-red-400/60" : check.known === false ? "border-amber-400/50" : "border-white/10"}`} placeholder="/internal-page or https://example.com" />
                      </div>
                      {check.ok && (
                        <a href={it.url} target="_blank" rel="noreferrer" className="p-2 rounded hover:bg-white/5 text-white/60 hover:text-white" aria-label={`Preview ${it.label || "link"}`}>
                          <Icons.ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button onClick={() => remove(it.id)} className="p-2 rounded hover:bg-red-500/10 text-white/60 hover:text-red-300" aria-label="Remove link">
                        <Icons.Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className={`text-[11px] pl-12 ${!check.ok || duplicate ? "text-red-300" : check.known === false ? "text-amber-300" : "text-white/40"}`}>
                      {duplicate ? "Duplicate URL in this menu. " : ""}{check.message}
                    </p>
                  </div>
                );
              })}
              {(current.items || []).length === 0 && (
                <div className="text-center text-sm text-white/40 py-8">No items. Click "Add link" to create one.</div>
              )}
            </div>
          </GlassCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
